import { useCallback, useState, useContext, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { GlobalStateContext } from "contexts/GlobalStateContext";
import { addIdForDataGrid, addNoDuplicate, moveRowTo, sortObj, sortObjDesc, validate } from "utils";
import { alertConfirmDelete, alertError, alertSuccess, alertWarning } from "components/Alert"
import { billingNoteApiNm } from "constants/billingNoteConstant";
import { initDialogData, initSearchInv } from "./initData";
import { cusApiNm } from "constants/cusConstant";
import { branchApiNm } from "branch/constant/branchApiNm";
import { printBillingNote365 } from "./printBillingNote365";





const useBillingNoteDialog = ({ selectedId, dialogOpen, onFinish }) => {

  const { ax, msData } = useContext(GlobalStateContext);
  const [dialogData, setDialogData] = useState(initDialogData)
  const [dataRows, setDataRows] = useState([])
  const [isPrintWht, setIsPrintWht] = useState(true)
  const [searchInvFilterData, setSearchInvFilterData] = useState(initSearchInv)
  const [searchInvData, setSearchInvData] = useState([])

  const validation = useMemo(() => ({
    BNNo: dialogData.BNNo === '',
    BNDte: dialogData.BNDte === '' || dialogData.BNDte === null || !dayjs(dialogData.BNDte).isValid(),
    RecvPayDte: dialogData.RecvPayDte === '' || dialogData.RecvPayDte === null || !dayjs(dialogData.RecvPayDte).isValid(),
  }), [dialogData]);

  const getDataAndPrint = useCallback((bnId) => {
    ax.post(branchApiNm.getBillingNote365Detail, { BNId: bnId, OrderBy: "invAll.InvNo" }).then(value => {
      if (value.data) {
        printBillingNote365({
          bnData: { ...dialogData, ...value.data[0] },
          companyData: msData.serverData.CompanyData
        })
      }
    })
  }, [ax, msData.serverData.CompanyData, dialogData])

  const handlePrintBillingNote = useCallback(() => {

    if (!validate(validation)) {
      return
    }

    if (selectedId > 0) {
      getDataAndPrint(selectedId)
    } else {
      ax.post(billingNoteApiNm.insertBillingNoteWithInvoice, {
        BNNo: dialogData.BNNo,
        BNDte: dayjs(dialogData.BNDte).format("YYYY-MM-DD"),
        Rmk: dialogData.Rmk,
        RecvPayDte: dayjs(dialogData.RecvPayDte).format("YYYY-MM-DD"),
        InvIds: dataRows.map(item => item.InvId)
      }).then(value => {
        if (value.data) {
          getDataAndPrint(value.data)
          onFinish()
        }
      })
    }
  }, [ax, dataRows, getDataAndPrint, dialogData, onFinish, selectedId, validation])

  const deleteBillingNote = useCallback(() => {
    alertConfirmDelete(() => {
      ax.post(billingNoteApiNm.deleteBillingNote, { BNId: selectedId }).then(value => {
        if (value.data) {
          onFinish()
        }
      })
    })
  }, [ax, selectedId, onFinish])

  const handleEditRmk = useCallback(() => {
    const postData = {
      BNId: selectedId,
      ColValues: {
        Rmk: dialogData.Rmk,
        RecvPayDte: dialogData.RecvPayDte ? dayjs(dialogData.RecvPayDte).format("YYYY-MM-DD") : null,
      },
      LogRmk: "บันทึกหมายเหตุและวันที่รับเงิน"
    }
    ax.post(billingNoteApiNm.updateBillingNoteColumn, postData).then(value => {
      if (value.data) {
        alertSuccess("บันทึกหมายเหตุและวันที่รับเงินเรียบร้อยแล้ว")

        onFinish()
      }
    })
  }, [ax, dialogData.Rmk, dialogData.RecvPayDte, selectedId, onFinish])

  const searchInvoice = useCallback((filterData) => {

    const postData = {
      ...filterData,
      IsNullBNId: 1,
    }
    ax.post(branchApiNm.getInvoiceTable365, postData).then(value => {
      if (value.data) {
        setSearchInvData(addIdForDataGrid(value.data, "InvId"))
      }
    })
    // setDialogData(initDialogData, selectedId)

  }, [ax])

  const handleSearchClick = useCallback(() => {

    searchInvoice(searchInvFilterData)
  }, [searchInvFilterData, searchInvoice])

  const selectInvoice = useCallback((row) => {
    if (dataRows.length > 0) {
      if (dataRows[0].CusId !== row.CusId) {
        alertError("กรุณาเลือกลูกค้าเดียวกันทั้งหมด")
        return
      }
    }
    if (dataRows.length === 0) {
      ax.post(cusApiNm.getCustomer, { CusId: row.CusId }).then(value => {
        if (value.data && value.data.length > 0) {
          const cus = value.data[0]
          setDialogData(o => ({ ...o, BillNm: cus.Name, BillAddr: cus.Addr, BillTaxId: cus.TaxId, BillBrchNo: cus.BrchNo }))
        }
      })
    }
    const data = { ...row, No: dataRows.length + 1 }
    setDataRows(o => addIdForDataGrid(sortObj(addNoDuplicate(o, [data], "InvId"), "InvNo"), "InvId"))
    setSearchInvData(o => o.filter(item => item.id !== row.id))

  }, [ax, dataRows])

  const selectInvoiceAll = useCallback(() => {
    if (searchInvData.length <= 0) return
    ax.post(cusApiNm.getCustomer, { CusId: searchInvData[0].CusId }).then(value => {
      if (value.data && value.data.length > 0) {
        const cus = value.data[0]
        setDialogData(o => ({ ...o, BillNm: cus.Name, BillAddr: cus.Addr, BillTaxId: cus.TaxId, BillBrchNo: cus.BrchNo }))
      }
    })
    setDataRows(o => {
      const newData = searchInvData.map((item, index) => ({ ...item, No: o.length + index + 1 }))
      return addIdForDataGrid(sortObj(addNoDuplicate(o, newData, "InvId"), "InvNo"), "InvId")
    })
    setSearchInvData([])
  }, [ax, searchInvData])

  const handleDeleteRow = useCallback((row) => {
    setDataRows(o => {
      const result = o.filter(item => item.id !== row.id)
      if (result.length === 0) {
        setDialogData(o => ({ ...o, BillNm: "", BillAddr: "", BillTaxId: "" }))
      }
      return result
    })
    setSearchInvData(o => [...o, row])

  }, [])

  const generateBNNo = useCallback(() => {
    if (dataRows.length === 0) {
      alertWarning("กรุณาเลือกอย่างน้อย 1 ใบแจ้งหนี้")
      return
    }
    ax.post(branchApiNm.generateBillingNoteNo, { CusId: dataRows[0].CusId }).then(value => {
      if (value.data) {
        setDialogData(o => ({
          ...o,
          BNNo: value.data.BNNo,
          RecvPayDte: dayjs().add(value.data.AvgDiff || 45, "day")
        }))
      }
    })
  }, [ax, dataRows])

  const insertBillingNote = useCallback(() => {
    if (!validate(validation)) {
      return
    }
    ax.post(billingNoteApiNm.insertBillingNoteWithInvoice, {
      BNNo: dialogData.BNNo,
      BNDte: dayjs(dialogData.BNDte).format("YYYY-MM-DD"),
      Rmk: dialogData.Rmk,
      RecvPayDte: dayjs(dialogData.RecvPayDte).format("YYYY-MM-DD"),
      InvIds: dataRows.map(item => item.InvId)
    }).then(value => {
      if (value.data) {
        onFinish()
      }
    })
  }, [ax, onFinish, dataRows, dialogData, validation])

  const handleRowOrderChange = useCallback((params) => {
    setDataRows(o => moveRowTo(o, params.oldIndex, params.targetIndex))
  }, [])

  const handleSortModelChange = useCallback((newSortModel) => {
    console.log("newSortModel::", newSortModel);
    if (newSortModel.length <= 0) return

    setDataRows(o => {
      const newData = [...o]
      if (newSortModel[0].sort === "asc")
        return sortObj(newData, newSortModel[0].field)
      else
        return sortObjDesc(newData, newSortModel[0].field)
    })
  }, [])
  useEffect(() => {

    if (dialogOpen) {
      if (selectedId > 0) {
        ax.post(branchApiNm.getBillingNote365Detail, { BNId: selectedId }).then(value => {
          if (value.data && value.data.length > 0) {
            const bnData = value.data[0]
            //delete bnData.Invs
            bnData.BillNm = bnData.Cus.Name
            bnData.BillAddr = bnData.Cus.Addr
            bnData.BillTaxId = bnData.Cus.TaxId
            bnData.BillBrchNo = bnData.Cus.BrchNo
            bnData.Lang = "TH"
            let isNoWht = false
            if (bnData.CusSNm.startsWith("GENTLE") || bnData.CusSNm.startsWith("GLOBAL") || bnData.CusSNm.startsWith("ASIA")) {
              isNoWht = true
            }
            console.log("isNoWht::", isNoWht)
            console.log("CusSNm::", bnData.CusSNm)
            bnData.Invs = bnData.Invs.map((item, index) => ({
              ...item,
              No: index + 1,
              id: item.InvId,
              ContNo: item.NumJobOrd > 1 ? `จำนวน ${item.NumJobOrd} เที่ยว` : item.ContNo || "",
              WhtPr1Total: isNoWht ? 0 : item.WhtPr1Total,
              WhtPr3Total: isNoWht ? 0 : item.WhtPr3Total,
              PayAmnt: isNoWht ? +item.GrandTotal : +item.GrandTotal - (item.WhtPr1Total || 0) - (item.WhtPr3Total || 0),
              TotalTrans: item.TransAmntTotal,
              TotalCharge: item.ChrgAmntTotal,
              TotalAdv: item.CusRcptAmntTotal,
              Brnd: [5, 6, 7].includes(item.ShpmTypId) ? item.Vssl : item.Brnd,
              CusInvNo: item.CusInvNo || item.ShRef1 || "",
            }))
            setDataRows(addIdForDataGrid([...bnData.Invs], "InvId"))
            delete bnData.Cus
            setDialogData(bnData)
          }
        })
      } else {
        // searchInvoice(initSearchInv)
        setDataRows([]);
        setDialogData({ ...initDialogData, BNDte: dayjs() })
        setSearchInvData([])
      }
    } else {
      setSearchInvData([])
      setDataRows([]);
      setDialogData({ ...initDialogData, BNDte: dayjs() })
    }
  }, [ax, dialogOpen, selectedId, searchInvoice])


  return {
    state: { dialogData, dataRows, msData, isPrintWht, searchInvFilterData, searchInvData, validation },
    fn: {
      deleteBillingNote, handleEditRmk, setDialogData, setIsPrintWht, handleSearchClick
      , handlePrintBillingNote, setSearchInvFilterData, selectInvoice, selectInvoiceAll, generateBNNo
      , insertBillingNote, handleDeleteRow, handleRowOrderChange, handleSortModelChange
    }
  }
}

export default useBillingNoteDialog